import { Component, OnInit, inject, isDevMode, signal, computed, effect } from '@angular/core';
import { Router } from '@angular/router';
import { 
    UntypedFormGroup, 
    UntypedFormControl, 
    Validators 
} from '@angular/forms';
import { environment } from 'src/environments/environment';

import { MatDialog, ModalWindowService } from '../Common/modal-window/modal-window.service';
import { WebInscripcionesInvitadosComponent } from '../web-inscripciones-invitados/web-inscripciones-invitados.component';
import { SHARED_IMPORTS, SHARED_DECLARATIONS } from "../Common/shared.config";

import { InscripcionesService, IPersonaRecord } from '../Common/inscripciones.service';
import { ColegiadosService } from '../Common/colegiados.service';
import { EmptyOrBeginCMAB, CodigoNoDuplicado } from '../Common/validators-cod-reserva';
import { DialogPasarelaPagoComponent } from './dialog-pasarela-pago/dialog-pasarela-pago.component';
import { DialogAvisoLegalComponent } from './dialog-aviso-legal/dialog-aviso-legal.component';
import { CambioIdiomaComponent } from "../cambio-idioma/cambio-idioma";

@Component({
    selector: 'app-web-inscripciones',
    templateUrl: './web-inscripciones.component.html',
    styleUrls: [
        '../Common/page.scss',
        '../Common/form.scss',
        './web-inscripciones.component.scss'
    ],
    standalone: true,
    imports: [...SHARED_IMPORTS, ...SHARED_DECLARATIONS, CambioIdiomaComponent]
})
export class WebInscripcionesComponent implements OnInit {
    private router = inject(Router);
    private _InscripcionesS = inject(InscripcionesService);
    private _dialog = inject(MatDialog);
    private _ColegiadosS = inject(ColegiadosService);
    private _modalWindowS = inject(ModalWindowService);

    // --- Signals de Estado ---
    public lista_invitados = signal<IPersonaRecord[]>(this._InscripcionesS.lista_invitados);
    public condiciones_legales = signal(false);
    
    // --- Signals que sustituyen a toSignal para evitar errores de sobrecarga ---
    public tipoInscripcionSignal = signal<string>('');
    public actoSocialSignal = signal<boolean>(false);

    public event_name = environment.event_name;
    public event_year = environment.event_year;

    private tarifario = [
        [0, 0], [0, 70], [90, 90], [90, 90], [90, 90], [90, 90]
    ];

    public form_inscripcion = new UntypedFormGroup({
        dni_validation_group: this._InscripcionesS.form_validation_dni,
        acto_social: new UntypedFormControl(false),
        nombre: new UntypedFormControl('', [Validators.required]),
        apellido1: new UntypedFormControl('', [Validators.required]),
        apellido2: new UntypedFormControl('', [Validators.required]),
        empresa: new UntypedFormControl('', [Validators.required]),
        tipo_empresa: new UntypedFormControl(''),
        cargo: new UntypedFormControl(''),
        direccion: new UntypedFormControl(''),
        ciudad: new UntypedFormControl(''),
        codigo_postal: new UntypedFormControl('', [Validators.required]),
        pais: new UntypedFormControl(''),
        email: new UntypedFormControl('', [
            Validators.required,
            Validators.pattern("^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$"),
        ]),
        telefono: new UntypedFormControl('', [Validators.required]),
        codigo_reserva: new UntypedFormControl('', {
            validators: [EmptyOrBeginCMAB()],
            asyncValidators: [CodigoNoDuplicado(this._InscripcionesS)]
        })
    });

    // --- Computed State (Ahora dependen de nuestros propios signals) ---
    public es_colegiado = computed(() => 
        parseInt(this.tipoInscripcionSignal()) === InscripcionesService.TIPO_INSCRIPCION_MEDIADOR
    );

    public importe_total = computed(() => {
        const tipo = parseInt(this.tipoInscripcionSignal());
        const acto = this.actoSocialSignal() ? 1 : 0;
        const invitados = this.lista_invitados();

        if (isNaN(tipo) || tipo === 0) return 0;

        let total = this.tarifario[tipo][acto] || 0;

        invitados.forEach((invitado, index) => {
            if (index === 0 && tipo === InscripcionesService.TIPO_INSCRIPCION_MEDIADOR) return; 
            const actoInv = (tipo === InscripcionesService.TIPO_INSCRIPCION_MEDIADOR) ? 1 : (invitado.acto_social ? 1 : 0);
            total += this.tarifario[tipo][actoInv] || 0;
        });

        return total;
    });

    constructor() {
        this._InscripcionesS.removePreviousSavedData();
        
        effect(() => {
            this._InscripcionesS.lista_invitados = this.lista_invitados();
        });

        this._setupFormSync();
    }

    ngOnInit(): void {
        // Inicializar los signals con los valores actuales del formulario
        const dniGroup = this.form_inscripcion.get('dni_validation_group');
        this.tipoInscripcionSignal.set(dniGroup?.get('tipo_inscripcion')?.value?.toString() || '');
        this.actoSocialSignal.set(!!this.form_inscripcion.get('acto_social')?.value);

        if (isDevMode()) {
            this._cargarDatosPrueba();
        }
    }

    private _setupFormSync() {
        // Sincronización general
        this.form_inscripcion.valueChanges.subscribe((val: any) => {
            if (val.acto_social !== undefined) {
                this.actoSocialSignal.set(!!val.acto_social);
            }
            Object.keys(val).forEach(key => {
                if (key !== 'dni_validation_group') {
                    this._InscripcionesS.setField(key, val[key]);
                }
            });
        });

        // Sincronización específica del DNI Group
        const dniGroup = this.form_inscripcion.get('dni_validation_group') as UntypedFormGroup;
        dniGroup.valueChanges.subscribe((val: any) => {
            if (val.tipo_inscripcion !== undefined) {
                this.tipoInscripcionSignal.set(val.tipo_inscripcion.toString());
            }
            Object.keys(val).forEach(key => {
                this._InscripcionesS.setField(key, val[key]?.toString().toUpperCase());
            });
        });
    }

    public addInvitado() {
        this._openWindowInvitados(this._InscripcionesS.getEmptyPersonaRecord());
    }

    public invitadoEdit(dni: string) {
        const inv_data = this.lista_invitados().find(e => e.dni === dni);
        if (inv_data) this._openWindowInvitados(inv_data);
    }

    public invitadoBorrar(dni: string) {
        this.lista_invitados.update(list => list.filter(e => e.dni !== dni));
    }

    public realizarPago() {
        this._InscripcionesS.setField('importe_factura', this.importe_total());
        this._InscripcionesS.setField('tipo_registro', 'web');

        this._InscripcionesS.guardaComoPendientes().subscribe({
            next: () => {
                if (this.importe_total() === 0) {
                    this.router.navigate(['/inscript/finish']);
                } else {
                    this._openWindowTPV();
                }
            },
            error: (e: any) => {
                console.error("Error guardando datos: ", e)
                this._modalWindowS.showMessageError('Error guardando datos: ' + e).subscribe();
            }
        });
    }

    private _openWindowInvitados(datos_persona: IPersonaRecord) {
        const ref = this._dialog.open(WebInscripcionesInvitadosComponent, {
            data: datos_persona,
            panelClass: 'message-window'
        });

        ref.afterClosed().subscribe((nuevo: IPersonaRecord | undefined) => {
            if (nuevo) {
                this.lista_invitados.update(actuales => {
                    const index = actuales.findIndex(e => e.dni === nuevo.dni);
                    if (index !== -1) {
                        const copia = [...actuales];
                        copia[index] = nuevo;
                        return copia;
                    }
                    return [...actuales, nuevo];
                });
            }
        });
    }

    private _openWindowTPV(): void {
        this._dialog.open(DialogPasarelaPagoComponent, {
            data: { importe: this.importe_total() },
            disableClose: true,
            panelClass: 'message-window'
        });
    }

    public mostrarAvisoLegal(): void {
        this._dialog.open(DialogAvisoLegalComponent, {
            data: {},
            disableClose: false,
            panelClass: 'message-window'
        });
    }

    private _cargarDatosPrueba() {
        const pruebas: any = {
            nombre: 'Gonzalo',
            apellido1: 'Gracia',
            apellido2: 'Blanco',
            empresa: 'La mia',
            codigo_postal: '08001',
            telefono: '650957703',
            email: 'gonzalo@petrasoft.es'
        };
        this.form_inscripcion.patchValue(pruebas);
        
        const dniGroup = this.form_inscripcion.get('dni_validation_group') as UntypedFormGroup;
        dniGroup.patchValue({
            dni: '14955087F',
            tipo_inscripcion: '2'
        });
        // Actualizar signals tras cargar pruebas
        this.tipoInscripcionSignal.set('2');
    }
}
